import React, { useEffect, useState } from 'react';
import { Col, Collapse as BsCollapse, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Button, Input, Label, Modal } from 'edozvola-frontend-lib/dist/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getIn } from 'formik';

const StagedConstructionForm = ({ setFieldValue, readOnly, formik }) => {
  const [openStages, setOpenStages] = useState<boolean[]>([]);
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const [stageToDelete, setStageToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (openStages.length === 0 && formik.values.stages.length > 0) {
      setOpenStages(formik.values.stages.map(() => true));
    }
  }, [formik.values.stages]);

  const toggleCollapse = index => {
    const updated = [...openStages];
    updated[index] = !updated[index];
    setOpenStages(updated);
  };

  const addPhaseToStage = (stageIndex: number) => {
    const updatedStages = [...formik.values.stages];
    const stage = updatedStages[stageIndex];

    const newPhases = [...stage.phases, { phaseNumber: stage.phases.length + 1, phaseValue: '' }];
    updatedStages[stageIndex] = { ...stage, phases: newPhases };

    setFieldValue('stages', updatedStages);
  };

  const deletePhaseFromStage = (stageIndex, phaseIndex) => {
    // create stages copy because formik doesn't get that form is now dirty
    const updatedStages = [...formik.values.stages].map((stage, i) => {
      if (i !== stageIndex) return stage;
      const updatedPhases = [...stage.phases];
      updatedPhases.splice(phaseIndex, 1);
      return {
        ...stage,
        phases: updatedPhases.map((phase, idx) => ({ ...phase, phaseNumber: idx + 1 })),
      };
    });

    formik.setFieldValue('stages', updatedStages);
  };

  const addStage = () => {
    const updatedStages = [...formik.values.stages];
    const newStageNumber = updatedStages.length + 1;

    const newStage = {
      stageNumber: newStageNumber,
      stageValue: '',
      phases: [],
    };

    updatedStages.push(newStage);
    setFieldValue('stages', updatedStages);
    setOpenStages(prev => [...prev, true]);
  };

  const deleteStage = (stageIndex: number) => {
    const updatedStages = [...formik.values.stages];
    updatedStages.splice(stageIndex, 1);

    const reindexedStages = updatedStages.map((stage, index) => ({
      ...stage,
      stageNumber: index + 1,
      phases: stage.phases.map((phase, pIndex) => ({
        ...phase,
        phaseNumber: pIndex + 1,
      })),
    }));

    setFieldValue('stages', reindexedStages);

    setOpenStages(prev => prev.filter((_, i) => i !== stageIndex));
  };

  const isStageValid = stageIndex => {
    const stageErrors = getIn(formik.errors, `stages[${stageIndex}]`);

    return !stageErrors;
  };

  const areAllStagesValid = () => {
    if (!formik.values.stages?.length) {
      return true;
    }

    return formik.values.stages?.every((s: any, i: any) => isStageValid(i));
  };

  return (
    <div>
      {formik.values.stages?.map((stage, sIndex) => (
        <div key={sIndex} className="border rounded p-3 mb-4 shadow-sm bg-white">
          <div className="d-flex justify-content-between align-items-center clickable hand" onClick={() => toggleCollapse(sIndex)}>
            <h5 className="mb-0">{`${stage.stageNumber}. etapa`}</h5>
            {!readOnly && (
              <FontAwesomeIcon
                icon="trash-alt"
                className="clickable"
                title="Obriši etapu"
                onClick={e => {
                  e.stopPropagation();
                  if (stage.phases?.length > 0) {
                    setStageToDelete(sIndex);
                    setDeleteModalShown(true);
                  } else {
                    deleteStage(sIndex);
                  }
                }}
              />
            )}
          </div>

          <div className="mt-2 mb-3">
            <Input
              type="text"
              value={stage.stageValue}
              disabled={readOnly}
              placeholder="Naziv etape"
              maxLength={255}
              error={getIn(formik.errors, `stages[${sIndex}].stageValue`) ? `${getIn(formik.errors, `stages[${sIndex}].stageValue`)}` : ''}
              onChange={e => formik.setFieldValue(`stages[${sIndex}].stageValue`, e.target.value)}
            />
          </div>

          <BsCollapse isOpen={openStages[sIndex]}>
            {stage.phases?.map((phase, pIndex) => (
              <Row key={pIndex} className="align-items-center mb-3 ps-3">
                <Col md={6}>
                  <Label labelText={`${phase.phaseNumber}. faza`} />
                  <div className={'red-addon-append'}>
                  <Input
                    disabled={readOnly}
                    value={phase.phaseValue}
                    error={
                      getIn(formik.errors, `stages[${sIndex}].phases[${pIndex}].phaseValue`)
                        ? `${getIn(formik.errors, `stages[${sIndex}].phases[${pIndex}].phaseValue`)}`
                        : ''
                    }
                    maxLength={255}
                    onChange={event => formik.setFieldValue(`stages[${sIndex}].phases[${pIndex}].phaseValue`, event.target.value)}
                    {...(!readOnly && {
                      addonAppend: (
                        <FontAwesomeIcon icon="trash" className="clickable hand" onClick={() => deletePhaseFromStage(sIndex, pIndex)} />
                      ),
                    })}
                  />
                  </div>
                </Col>
              </Row>
            ))}
            {!readOnly && (
              <Button
                label="Dodaj fazu"
                color="info"
                prependIcon={'plus'}
                size="lg"
                disabled={!formik.values.phasedConstructionType?.[0]?.id || !isStageValid(sIndex)}
                onClick={() => addPhaseToStage(sIndex)}
              />
            )}
          </BsCollapse>

          <Modal scrollable isOpen={deleteModalShown} size={'lg'} toggle={() => setDeleteModalShown(false)} centered>
            <ModalHeader>
              <span>Brisanje etape koja ima faze</span>
            </ModalHeader>
            <ModalBody>
              <div>Brisanjem etape obrisat će se i sve njezine faze! Želite li nastaviti?</div>
            </ModalBody>
            <ModalFooter>
              <Button
                size={'lg'}
                label="Odustani"
                outline
                onClick={() => {
                  setStageToDelete(null);
                  setDeleteModalShown(false);
                }}
              />
              <Button
                size={'lg'}
                label="Obriši"
                color={'danger'}
                onClick={() => {
                  if (stageToDelete !== null) {
                    deleteStage(stageToDelete);
                    setStageToDelete(null);
                    setDeleteModalShown(false);
                  }
                }}
              />
            </ModalFooter>
          </Modal>
        </div>
      ))}

      {!readOnly && (
        <Button
          label="Dodaj etapu"
          prependIcon={'plus'}
          color="info"
          size="lg"
          disabled={!formik.values.phasedConstructionType?.[0]?.id || !areAllStagesValid()}
          onClick={addStage}
        />
      )}
    </div>
  );
};

export default StagedConstructionForm;
